<template>
 <a-row v-if="advice && advice.packageIDs.length" class="mb-5">
  <a-col :xs="24" class="mb-2">
   <label>Pakketadvies isolatie</label>
   <a-spin v-if="loading" size="small" style="margin-left: 10px"/>
  </a-col>
  <a-col :xs="24">
   <a-alert v-if="advice.recommendedID" :message="'Advies: ' + advice.recommendedName" show-icon type="success">
    <template #description>
     Op basis van de ingevulde m². Dit pakket is in het winkelmandje gezet.
     <div v-if="advice.surcharge > 0">
      Meer m² dan het pakket dekt: meerprijs <strong>{{ formatMoney(advice.surcharge) }}</strong> excl. btw, al verwerkt in de pakketprijs.
     </div>
     <table v-if="coverageRows.length" class="isolation-coverage">
      <tr>
       <th>Maatregel</th>
       <th>Ingevuld</th>
       <th>In het pakket</th>
       <th>Extra (meerprijs)</th>
      </tr>
      <tr v-for="row in coverageRows" :key="row.measure">
       <td>{{ row.label }}</td>
       <td>{{ formatM2(row.m2) }}</td>
       <td>{{ formatM2(row.included) }}</td>
       <td>{{ row.extra > 0 ? formatM2(row.extra) : '-' }}</td>
      </tr>
     </table>
    </template>
   </a-alert>
   <a-alert v-else-if="advice.message" :message="advice.message" show-icon type="warning"/>
  </a-col>
  <a-col v-if="standardRows.length" :xs="24" class="mt-2">
   <small><strong>Standaard in de pakketten</strong> (m² als alleen die maatregel wordt uitgevoerd; bij meerdere maatregelen
    wordt de ruimte naar verhouding verdeeld)</small>
   <table class="isolation-coverage">
    <tr>
     <th>Pakket</th>
     <th>Max. maatregelen</th>
     <th v-for="measure in MEASURES" :key="measure.key">{{ measure.label }}</th>
    </tr>
    <tr v-for="pkg in standardRows" :key="pkg.id">
     <td>{{ pkg.name }}</td>
     <td>{{ pkg.maxMeasures || '-' }}</td>
     <td v-for="measure in MEASURES" :key="measure.key">{{ pkg.maxM2[measure.key] ? 'tot ' + formatM2(pkg.maxM2[measure.key]) : '-' }}</td>
    </tr>
   </table>
  </a-col>
 </a-row>
</template>

<script>
import {computed, ref, watch} from "vue";
import {useStore} from "vuex";
import {DataService} from "../../../config/dataService/dataService";

const ISOLATION_FIELDS = ['isolatie_spouw_m2', 'isolatie_vloer_m2', 'isolatie_bodem_m2', 'isolatie_dak_m2'];
const MEASURES = [
 {key: 'SPOUW', label: 'Spouw'},
 {key: 'VLOER', label: 'Vloer'},
 {key: 'BODEM', label: 'Bodem'},
 {key: 'DAK', label: 'Dak'},
];

const IsolationPackageAdvice = {
 name: 'IsolationPackageAdvice',
 props: {
  productId: {type: [String, Number], required: true},
 },
 // apply: {packageIDs, recommendedID, surcharge} -> de flow zet het geadviseerde pakket in het mandje
 emits: ['apply'],
 setup(props, {emit}) {
  const {state} = useStore();
  const loading = ref(false);
  const advice = ref(null);
  let timer = null;
  let requestId = 0;

  const isolationM2 = computed(() => {
   const roi = state.shop.order?.extraData?.roiInput || {};
   return Object.fromEntries(ISOLATION_FIELDS.map(field => [field, roi[field] || null]));
  });

  const fetchAdvice = async () => {
   const id = ++requestId;
   loading.value = true;
   try {
    const {data} = await DataService.post('offerflow/shop/isolation-advice', {
     productID: props.productId,
     roiInput: isolationM2.value,
    });
    // Alleen het antwoord op de laatste wijziging gebruiken
    if (id !== requestId) return;
    advice.value = data;
    if (data.recommendedID) emit('apply', data);
   } catch (e) {
    if (id === requestId) advice.value = null;
   } finally {
    if (id === requestId) loading.value = false;
   }
  };

  watch(() => JSON.stringify(isolationM2.value), () => {
   clearTimeout(timer);
   timer = setTimeout(fetchAdvice, 500);
  }, {immediate: true});

  const coverageRows = computed(() => MEASURES
   .filter(measure => advice.value?.coverage?.[measure.key])
   .map(measure => ({measure: measure.key, label: measure.label, ...advice.value.coverage[measure.key]})));

  const standardRows = computed(() => (advice.value?.packages || []).filter(pkg => Object.keys(pkg.maxM2 || {}).length));

  const formatM2 = m2 => `${new Intl.NumberFormat('nl-NL', {maximumFractionDigits: 1}).format(m2 || 0)} m²`;
  const formatMoney = amount => new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(amount || 0);

  return {
   loading,
   advice,
   MEASURES,
   coverageRows,
   standardRows,
   formatM2,
   formatMoney,
  };
 },
};

export default IsolationPackageAdvice;
</script>

<style scoped>
.isolation-coverage {
 margin-top: 8px;
 border-collapse: collapse;
}

.isolation-coverage th,
.isolation-coverage td {
 padding: 2px 16px 2px 0;
 text-align: left;
}
</style>
